import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { FLOWERS } from '@hero/constants';

const PREVIEW_IMAGE = '/og-image.png';

const OG = {
  image: PREVIEW_IMAGE,
  type: 'website',
  locale: 'en_US',
  siteName: FLOWERS.name,
  url: FLOWERS.url
};

const TWITTER = {
  image: PREVIEW_IMAGE,
  creator: FLOWERS.name,
  site: FLOWERS.url
};

export function Seo({
  // defaults
  author = FLOWERS.name,
  charSet = 'utf-8',
  viewport = 'width=device-width,initial-scale=1',

  // dynamic
  title,
  description,
  robots,
  url = FLOWERS.url,

  og = OG,
  twitter = TWITTER
}) {
  const { i18n, t } = useTranslation();
  const theme = useTheme();
  const lang = i18n.language || 'en';

  return (
    <Helmet>
      <meta charSet={charSet} />
      <meta name="author" content={author} />
      <meta name="viewport" content={viewport} />
      <meta name="publisher" content={author} />
      <html lang={lang} />

      <link rel="icon" type="image/x-icon" href={theme?.images?.favicon} />
      <link rel="icon" type="image/png" sizes="32x32" href={theme?.images?.favicon16} />
      <link rel="icon" type="image/png" sizes="16x16" href={theme?.images?.favicon32} />

      {title && (
        <title>
          {title} | {t('metaTitle')}
        </title>
      )}
      {description && <meta name="description" content={description} />}
      {robots && <meta name="robots" content={robots} />}
      <link rel="canonical" href={url} />

      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={og.image} />
      <meta property="og:type" content={og.type} />
      <meta property="og:locale" content={og.locale} />
      <meta property="og:site_name" content={og.siteName} />
      <meta property="og:url" content={og.url} />

      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={twitter.image} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:creator" content={twitter.creator} />
      <meta name="twitter:site" content={twitter.site} />
    </Helmet>
  );
}

Seo.propTypes = {
  // defaults
  author: PropTypes.string,
  charSet: PropTypes.string,
  lang: PropTypes.string,
  viewport: PropTypes.string,

  // dynamic
  title: PropTypes.string,
  description: PropTypes.string,
  url: PropTypes.string,
  robots: PropTypes.string,

  og: PropTypes.shape({
    image: PropTypes.string,
    type: PropTypes.string,
    locale: PropTypes.string,
    siteName: PropTypes.string,
    url: PropTypes.string
  }),
  twitter: PropTypes.shape({
    creator: PropTypes.string,
    image: PropTypes.string,
    site: PropTypes.string
  })
};
